import i18n from '@fiverr/i18n/singleton';
import type { EagerJobInfo, EagerJobsTbdSdk } from '@fiverr-private/jtbd_sdk';
import pathfinder from '@fiverr-private/pathfinder';
import { isBusinessInfoBiEnrichment, type BusinessInfoBiEnrichment } from '../types';
import { fetchCompletionPercentage } from './utils/fetchCompletionPercentage';
import { logError } from './utils/logger';
import { trackClick, trackImpression } from './utils/mixpanel';
import { ICON_URL } from './constants';

const COPIES_PREFIX = 'jobs_tbd.business_info';
const TTL_30_DAYS = 30;

export const BusinessInfoCardImpl = {
    getJobInfo: async (args) => {
        const { context } = args;
        const { locale } = context;

        const url = args.externalServicesInfo?.buyersKimera?.url;

        if (!url) {
            logError({ message: 'No "buyersKimeraUrl", skipping data fetching for BusinessInfoJtbdCard' });

            return getResult({ locale, completionPercentage: null });
        }

        const completionPercentage = await fetchCompletionPercentage(url);

        return getResult({ locale, completionPercentage });
    },
    cardCallbacks: {
        onCardClick: (args) => {
            // The type guard is required here for additional type safety of the implementation,
            // because the SDK doesn't give an ability to pass a generic type for the BiEnrichment object structure
            if (!args || !args.biEnrichment || !isBusinessInfoBiEnrichment(args.biEnrichment)) {
                logError({
                    message: 'onCardClick - No expected "biEnrichment" data. Implementation is incorrect',
                    details: { args },
                });

                return;
            }

            const { completionPercentage } = args.biEnrichment;

            trackClick({ completionPercentage });
        },
        onCardImpression: (args) => {
            // The type guard is required here for additional type safety of the implementation,
            // because the SDK doesn't give an ability to pass a generic type for the BiEnrichment object structure
            if (!args || !args.biEnrichment || !isBusinessInfoBiEnrichment(args.biEnrichment)) {
                logError({
                    message: 'onCardImpression - No expected "biEnrichment" data. Implementation is incorrect',
                    details: { args },
                });

                return;
            }

            const { completionPercentage } = args.biEnrichment;

            trackImpression({ completionPercentage });
        },
    },
} satisfies EagerJobsTbdSdk;

const getResult = ({
    locale,
    completionPercentage,
}: {
    locale: string;
    completionPercentage: number | null;
}): EagerJobInfo => {
    const shouldDisplayJob = completionPercentage !== null && completionPercentage < 100;

    if (!shouldDisplayJob) {
        return {
            shouldDisplayJob,
        };
    }

    return {
        shouldDisplayJob,
        dismissInfo: {
            isDismissible: true,
            jtbdTTL: TTL_30_DAYS,
        },

        headerText: i18n.t(`${COPIES_PREFIX}.header_text`, { $scope: locale }),
        jobPages: [
            {
                title: getTitle({ locale, completionPercentage }),
                subtitle: getSubtitle({ locale, completionPercentage }),
                ctaText: ctaText({ locale, completionPercentage }),
                iconUrl: ICON_URL,
                biEnrichment: {
                    completionPercentage,
                } satisfies BusinessInfoBiEnrichment,
            },
        ],
        clickAction: {
            type: 'link',
            href: pathfinder('my_buyer_profile'),
        },
    };
};

const getTitle = ({
    locale,
    completionPercentage,
}: {
    locale: string;
    completionPercentage: number | null;
}): string => {
    if (completionPercentage === null || completionPercentage === 0) {
        return i18n.t(`${COPIES_PREFIX}.title.not_started`, { $scope: locale });
    }

    return i18n.t(`${COPIES_PREFIX}.title.partially_completed`, {
        $scope: locale,
        completionPercentage,
    });
};

const getSubtitle = ({
    locale,
    completionPercentage,
}: {
    locale: string;
    completionPercentage: number | null;
}): string => {
    if (completionPercentage === null || completionPercentage === 0) {
        return i18n.t(`${COPIES_PREFIX}.subtitle.not_started`, { $scope: locale });
    }

    return i18n.t(`${COPIES_PREFIX}.subtitle.partially_completed`, { $scope: locale });
};

const ctaText = ({ locale, completionPercentage }: { locale: string; completionPercentage: number | null }): string => {
    if (completionPercentage === null || completionPercentage === 0) {
        return i18n.t(`${COPIES_PREFIX}.cta.not_started`, { $scope: locale });
    }

    return i18n.t(`${COPIES_PREFIX}.cta.partially_completed`, { $scope: locale });
};
